import type { UseQueryResult } from '@tanstack/react-query'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ReactElement } from 'react'
import { useState } from 'react'
import type { HouseholdEntity } from '@/api/entities'
import type { EntitiesResponse } from '~/api/client'
import { api } from '~/api/client'
import { Button } from '~/components/ui/Controls'
import { Field } from '~/components/ui/Modal'

/** The menu's own entry for naming an entity the household does not have yet. */
const ADD_NEW = '__add_new__'

export function useEntities(): UseQueryResult<EntitiesResponse, Error> {
  return useQuery({ queryKey: ['entities'], queryFn: () => api.entities() })
}

interface EntityChoiceProps {
  label: string
  /** What an unchosen entity reads as, which differs by whether one is required. */
  placeholder: string
  value: string
  required?: boolean
  /**
   * Given where the dialog labels its controls by id. The hand-entry forms
   * dress their own labels around the control instead, so the choice cannot be
   * one shape only without one of the two dialogs it appears in losing its
   * label.
   */
  id?: string
  onChange: (entityKey: string) => void
}

/**
 * Whose money this is, asked of the household rather than compiled in.
 *
 * Every household has its own entities and a new one has exactly the person the
 * bootstrap puts in it, so a list in the bundle offers names nobody has and
 * withholds the only name that resolves. An attribution the household cannot
 * resolve is accepted by the write and then refused by the nightly, which drops
 * every hand-entered holding, not only the misfiled one.
 */
export function EntityChoice({
  label,
  placeholder,
  value,
  required,
  id,
  onChange,
}: EntityChoiceProps): ReactElement {
  const { data, isPending, error } = useEntities()
  const entities = data?.entities ?? []
  const [adding, setAdding] = useState(false)

  // An account already naming an entity the household does not have is the
  // state this whole choice exists to repair, so the key stays visible and
  // wrong instead of being quietly resubmitted as itself.
  const stranded = value !== '' && !isPending && !entities.some((one) => one.key === value)

  const select = (
    <select
      id={id}
      value={value}
      onChange={(e) => {
        if (e.target.value === ADD_NEW) {
          setAdding(true)
          return
        }
        onChange(e.target.value)
      }}
      disabled={isPending}
      {...(required === true ? { required: true } : {})}
    >
      <option value="">{isPending ? 'Loading…' : placeholder}</option>
      {entities.map((entity) => (
        <option key={entity.key} value={entity.key}>
          {entity.name}
        </option>
      ))}
      {stranded && <option value={value}>{value} — not in this household</option>}
      {/* Here rather than only in Settings: the moment somebody finds the
          entity missing is the moment they are choosing it, and sending them
          away to make it loses the dialog they were half way through. */}
      {!isPending && <option value={ADD_NEW}>Add new…</option>}
    </select>
  )
  const refusal = (
    <>
      {error && <small className="nw-error">Could not load entities: {error.message}</small>}
      {adding && (
        <NewEntity
          idPrefix={id ?? label}
          onCancel={() => setAdding(false)}
          onAdded={(entity) => {
            setAdding(false)
            onChange(entity.key)
          }}
        />
      )}
    </>
  )

  if (id !== undefined) {
    return (
      <Field id={id} label={label} width="set">
        {select}
        {refusal}
      </Field>
    )
  }

  return (
    <label>
      {label}
      {select}
      {refusal}
    </label>
  )
}

/**
 * Naming a new entity without leaving the choice that needed it.
 *
 * Buttons rather than a form, because this choice is drawn inside other forms
 * and a form inside a form is not one the browser will submit.
 */
function NewEntity({
  idPrefix,
  onCancel,
  onAdded,
}: {
  idPrefix: string
  onCancel: () => void
  onAdded: (entity: HouseholdEntity) => void
}): ReactElement {
  const client = useQueryClient()
  const [name, setName] = useState('')
  // A company is the usual reason to be here — a login or an account that is
  // not the person's own — but the choice is still asked, never assumed.
  const [kind, setKind] = useState<HouseholdEntity['kind']>('company')
  const create = useMutation({
    mutationFn: (input: { name: string; kind: HouseholdEntity['kind'] }) => api.createEntity(input),
    onSuccess: async (entity) => {
      await client.invalidateQueries({ queryKey: ['entities'] })
      onAdded(entity)
    },
  })
  const trimmed = name.trim()
  const nameId = `${idPrefix}-new-entity-name`

  return (
    <span
      className="nw-new-entity"
      role="group"
      aria-label="Add an entity"
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--nw-space-2)', marginTop: 'var(--nw-space-2)' }}
    >
      <label htmlFor={nameId}>Name</label>
      <input
        id={nameId}
        type="text"
        autoComplete="off"
        value={name}
        disabled={create.isPending}
        placeholder="1337 ApS"
        onChange={(event) => setName(event.target.value)}
      />
      <select
        aria-label="What it is"
        value={kind}
        disabled={create.isPending}
        onChange={(event) => setKind(event.target.value as HouseholdEntity['kind'])}
      >
        <option value="company">Company</option>
        <option value="person">Person</option>
      </select>
      <Button
        kind="primary"
        disabled={create.isPending || trimmed === ''}
        onClick={() => create.mutate({ name: trimmed, kind })}
      >
        {create.isPending ? 'Adding…' : 'Add'}
      </Button>
      <Button disabled={create.isPending} onClick={onCancel}>
        Cancel
      </Button>
      {create.error && <small className="nw-error">{create.error.message}</small>}
    </span>
  )
}

/** The household's own word for whose money this is, with the key as a fallback. */
export function EntityName({ entityKey }: { entityKey: string }): ReactElement {
  const { data } = useEntities()
  const named = data?.entities.find((entity) => entity.key === entityKey)
  return <>{named?.name ?? entityKey}</>
}
